/**
 * MietwagenEditableSection.tsx — Wiederverwendbare editierbare Section
 * für alle Mietwagen-Prozessschritte (Übergabe, Laufzeit, Rückgabe, Schadenscheck, Verfügbarkeit)
 * 
 * Jede Section hat einen Anzeige- und Bearbeitungsmodus.
 * Felder werden dynamisch aus einer Konfiguration generiert.
 */
import { useState, useCallback } from 'react';
import { Pencil, Check, X, Loader2, Save } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// ─── Typen ────────────────────────────────────────────────────────────────────

export interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'select' | 'textarea' | 'checkbox';
  options?: { value: string; label: string }[];
  placeholder?: string;
  suffix?: string;
  colSpan?: number; // 1 oder 2 (volle Breite)
  group?: string; // Gruppierung
}

interface MietwagenEditableSectionProps {
  title: string;
  description?: string;
  akte: any;
  fields: FieldConfig[];
  infoHint?: string;
  onUpdate: () => void;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function getNestedValue(obj: any, key: string): any {
  return obj?.[key] ?? '';
}

function formatDisplayValue(value: any, field: FieldConfig): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field.type === 'date' && value) {
    try { return new Date(value).toLocaleDateString('de-DE'); } catch { return String(value); }
  }
  if (field.type === 'number' && typeof value === 'number') {
    return value.toLocaleString('de-DE', { minimumFractionDigits: field.suffix === '€' ? 2 : 0 }) + (field.suffix ? ` ${field.suffix}` : '');
  }
  if (field.type === 'checkbox') return value ? 'Ja' : 'Nein';
  if (field.type === 'select' && field.options) {
    const opt = field.options.find(o => o.value === String(value));
    return opt ? opt.label : String(value);
  }
  return String(value) + (field.suffix ? ` ${field.suffix}` : '');
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function MietwagenEditableSection({
  title, description, akte, fields, infoHint, onUpdate
}: MietwagenEditableSectionProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});

  const startEditing = useCallback(() => {
    const data: Record<string, any> = {};
    fields.forEach(f => {
      const val = getNestedValue(akte, f.key);
      data[f.key] = val ?? (f.type === 'checkbox' ? false : '');
    });
    setFormData(data);
    setEditing(true);
  }, [akte, fields]);

  const cancelEditing = () => {
    setEditing(false);
    setFormData({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Record<string, any> = {};
      fields.forEach(f => {
        let val = formData[f.key];
        if (f.type === 'number' && val !== '' && val !== null && val !== undefined) {
          val = parseFloat(String(val).replace(',', '.'));
          if (isNaN(val)) val = null;
        }
        if (val === '') val = null;
        updates[f.key] = val;
      });

      const { error } = await supabase
        .from('akten')
        .update(updates)
        .eq('id', akte.id);

      if (error) throw error;
      toast.success(`${title} gespeichert`);
      setEditing(false);
      onUpdate();
    } catch (err: any) {
      toast.error('Fehler beim Speichern', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Felder nach Gruppen sortieren
  const groups = new Map<string, FieldConfig[]>();
  fields.forEach(f => {
    const group = f.group || '_default';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(f);
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">{title}</h3>
          {description && <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>}
        </div>
        {!editing ? (
          <button
            onClick={startEditing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Bearbeiten
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEditing}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Speichern
            </button>
          </div>
        )}
      </div>

      {/* Info-Hint */}
      {infoHint && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-[11px] text-blue-700">{infoHint}</p>
        </div>
      )}

      {/* Felder */}
      {Array.from(groups.entries()).map(([groupName, groupFields]) => (
        <div key={groupName}>
          {groupName !== '_default' && (
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 mt-3">{groupName}</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {groupFields.map(field => (
              <div
                key={field.key}
                className={`${field.colSpan === 2 ? 'col-span-2' : ''} ${editing ? '' : 'bg-gray-50 rounded-lg p-3'}`}
              >
                {editing ? (
                  /* Bearbeitungsmodus */
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-1">
                      {field.label}
                    </label>
                    {field.type === 'text' && (
                      <input
                        type="text"
                        value={formData[field.key] || ''}
                        onChange={e => updateField(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    )}
                    {field.type === 'date' && (
                      <input
                        type="date"
                        value={formData[field.key] || ''}
                        onChange={e => updateField(field.key, e.target.value)}
                        className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    )}
                    {field.type === 'number' && (
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData[field.key] || ''}
                          onChange={e => updateField(field.key, e.target.value)}
                          placeholder={field.placeholder || '0'}
                          className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-8"
                        />
                        {field.suffix && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">{field.suffix}</span>
                        )}
                      </div>
                    )}
                    {field.type === 'select' && (
                      <select
                        value={formData[field.key] || ''}
                        onChange={e => updateField(field.key, e.target.value)}
                        className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      >
                        <option value="">— Auswählen —</option>
                        {field.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                    {field.type === 'textarea' && (
                      <textarea
                        value={formData[field.key] || ''}
                        onChange={e => updateField(field.key, e.target.value)}
                        placeholder={field.placeholder || ''}
                        rows={3}
                        className="w-full px-3 py-2 text-[12px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                      />
                    )}
                    {field.type === 'checkbox' && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData[field.key]}
                          onChange={e => updateField(field.key, e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-[12px] text-gray-700">{field.placeholder || 'Ja'}</span>
                      </label>
                    )}
                  </div>
                ) : (
                  /* Anzeigemodus */
                  <>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase">{field.label}</p>
                    <p className={`text-[13px] font-medium mt-1 ${
                      getNestedValue(akte, field.key) ? 'text-gray-800' : 'text-gray-300'
                    }`}>
                      {formatDisplayValue(getNestedValue(akte, field.key), field)}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
